import { IncomingMessage, ServerResponse } from 'http'
import jsCookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'

import { EBonhamsCookies } from '@nx/global-types'
import { getDomainNameFromHost } from '@nx/helpers'

const SESSIONID = 'sessionid'

function getClientHost() {
  return typeof window !== 'undefined'
    ? window.location.host
    : 'www.bonhams.com'
}

interface SetAuthCookiesProps {
  accessToken: string
  refreshToken: string
}

export function setAuthCookies({
  accessToken,
  refreshToken,
}: SetAuthCookiesProps) {
  const cookieOptions = {
    domain: getDomainNameFromHost(getClientHost()),
    path: '/',
    secure: false,
  }

  const accessExpiry = jwtDecode<{ exp: number }>(accessToken).exp
  jsCookies.set(EBonhamsCookies.access, accessToken, {
    ...cookieOptions,
    expires: new Date(accessExpiry * 1000),
  })

  const refreshExpiry = jwtDecode<{ exp: number }>(refreshToken).exp
  jsCookies.set(EBonhamsCookies.refresh, refreshToken, {
    ...cookieOptions,
    expires: new Date(refreshExpiry * 1000),
  })
}

interface SetSSRAuthCookiesProps {
  accessToken: string
  refreshToken: string
  req: IncomingMessage
  res: ServerResponse
}

export async function setSSRAuthCookies({
  accessToken,
  refreshToken,
  req,
  res,
}: SetSSRAuthCookiesProps) {
  // dynamically importing cookies to avoid an import of 'crypto' lib on the client side
  const { default: Cookies } = await import('cookies')
  const cookieJar = new Cookies(req, res)

  const cookieOptions: Parameters<typeof cookieJar.set>[2] = {
    domain: getDomainNameFromHost(req.headers.host || 'www.bonhams.com'),
    path: '/',
    secure: false,
    httpOnly: false,
  }

  const accessExpiry = jwtDecode<{ exp: number }>(accessToken).exp
  cookieJar.set(EBonhamsCookies.access, accessToken, {
    ...cookieOptions,
    expires: new Date(accessExpiry * 1000),
  })
  const refreshExpiry = jwtDecode<{ exp: number }>(refreshToken).exp
  cookieJar.set(EBonhamsCookies.refresh, refreshToken, {
    ...cookieOptions,
    expires: new Date(refreshExpiry * 1000),
  })
}

export function removeAuthCookies() {
  const host = getClientHost()

  jsCookies.remove(EBonhamsCookies.access, {
    domain: getDomainNameFromHost(host),
    path: '/',
  })
  jsCookies.remove(EBonhamsCookies.refresh, {
    domain: getDomainNameFromHost(host),
    path: '/',
  })
}

export async function removeSSRAuthCookies(
  req: IncomingMessage,
  res: ServerResponse
) {
  const cookieOptions = {
    domain: getDomainNameFromHost(req.headers.host || 'www.bonhams.com'),
    expires: new Date(Date.now()),
    path: '/',
    httpOnly: false,
  }

  const ldcCookieOpts = {
    ...cookieOptions,
    domain: undefined,
  }

  const expirationDate = new Date()
  expirationDate.setDate(expirationDate.getDate() - 1)

  // dynamically importing cookies to avoid an import of 'crypto' lib on the client side
  const { default: Cookies } = await import('cookies')
  const cookieJar = new Cookies(req, res)

  cookieJar.set(EBonhamsCookies.access, 'delete', cookieOptions)
  cookieJar.set(EBonhamsCookies.refresh, 'delete', cookieOptions)
  cookieJar.set(SESSIONID, 'delete', ldcCookieOpts)
}
